import { __assign, __awaiter, __generator, __spreadArray } from "tslib";
/**
 * Generic helper for fetching all pages from a cursor-based REST API endpoint.
 *
 * This is the REST API equivalent of listAllPaginated from @tumelo/common,
 * designed for APIs that use cursor-based pagination instead of page tokens.
 *
 * Automatically merges array fields and Map fields across pages.
 *
 * @template TRequest - The request type (must include cursor and pageSize)
 * @template TResponse - The response type from a single page
 *
 * @param fetchPage - Function that fetches a single page of results
 * @param cursorField - Name of the field in the response that contains the next page cursor
 * @param request - The base request parameters (cursor will be overridden during pagination)
 *
 * @returns Promise resolving to the merged response with all pages combined
 *
 * @example
 * ```ts
 * // Automatically merges arrays and Maps across all pages
 * const result = await listAllPaginatedRest(
 *   listFilteredProxyIntermediaryBallots,
 *   'nextPageToken',
 *   { pageSize: 100, hasChecker: false }
 * )
 * // result.proxyIntermediaryBallotNames contains all names from all pages
 * // result.ballotDataMap contains merged data from all pages
 * ```
 */
export function listAllPaginatedRest(fetchPage, cursorField, request) {
    return __awaiter(this, void 0, void 0, function () {
        var cursor, firstResponse, accumulatedArrays, accumulatedMaps, response, _i, _a, _b, key, value, _loop_1, _c, _d, _e, key, value, nextCursor, result, _f, _g, _h, key, value, _j, _k, _l, key, value;
        return __generator(this, function (_m) {
            switch (_m.label) {
                case 0:
                    cursor = request.cursor;
                    firstResponse = null;
                    accumulatedArrays = {};
                    accumulatedMaps = {};
                    _m.label = 1;
                case 1:
                    if (!true) return [3 /*break*/, 3];
                    return [4 /*yield*/, fetchPage(__assign(__assign({}, request), { cursor: cursor }))];
                case 2:
                    response = _m.sent();
                    if (!firstResponse) {
                        firstResponse = response;
                        // Initialize accumulators based on first response
                        for (_i = 0, _a = Object.entries(response); _i < _a.length; _i++) {
                            _b = _a[_i], key = _b[0], value = _b[1];
                            if (key === cursorField)
                                continue;
                            if (Array.isArray(value)) {
                                accumulatedArrays[key] = __spreadArray([], value, true);
                            }
                            else if (value instanceof Map) {
                                accumulatedMaps[key] = new Map(value);
                            }
                        }
                    }
                    else {
                        _loop_1 = function (key, value) {
                            var _o;
                            if (key === cursorField)
                                return "continue";
                            if (Array.isArray(value)) {
                                (_o = accumulatedArrays[key]).push.apply(_o, value);
                            }
                            else if (value instanceof Map) {
                                var mapValue = value;
                                mapValue.forEach(function (v, k) {
                                    accumulatedMaps[key].set(k, v);
                                });
                            }
                        };
                        // Merge subsequent responses
                        for (_c = 0, _d = Object.entries(response); _c < _d.length; _c++) {
                            _e = _d[_c], key = _e[0], value = _e[1];
                            _loop_1(key, value);
                        }
                    }
                    nextCursor = response[cursorField];
                    if (!nextCursor) {
                        return [3 /*break*/, 3];
                    }
                    cursor = nextCursor;
                    return [3 /*break*/, 1];
                case 3:
                    result = __assign({}, firstResponse);
                    for (_f = 0, _g = Object.entries(accumulatedArrays); _f < _g.length; _f++) {
                        _h = _g[_f], key = _h[0], value = _h[1];
                        result[key] = value;
                    }
                    for (_j = 0, _k = Object.entries(accumulatedMaps); _j < _k.length; _j++) {
                        _l = _k[_j], key = _l[0], value = _l[1];
                        result[key] = value;
                    }
                    result[cursorField] = ''; // Clear the cursor in final result
                    return [2 /*return*/, result];
            }
        });
    });
}
